import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Grid } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import { useAccount, useChainId } from 'wagmi';
import { getConnections, waitForTransactionReceipt } from '@wagmi/core';
import Web3 from 'web3';
import BigNumber from 'bignumber.js';
import {
  MdOutlineErrorOutline,
  MdOutlineKeyboardArrowDown,
} from 'react-icons/md';
import { BrowserProvider, Provider, Signer } from 'zksync-ethers';

import OZKLogo from '../../../Assets/images/Icons/OZKLogo.png';
import OZKTokenIcon from '../../../Assets/images/Icons/OZKTokenIcon.png';
import DoubleArrow from '../../../Assets/images/double-arrow.png';
import DAIToken from '../../../ContractABI/DAIToken.json';
import ConnectButton from '../../../WagmiProvider/ConnectButton';
import {
  REACT_APP_BLOCK_EXPLORER_OPEN_ZK_URL,
  REACT_APP_OPENZK_CHAIN_ID,
  REACT_APP_OPENZK_RPC,
} from '../../../Config/Config';
import { toast } from 'react-toastify';
import CustomModal from '../../../Components/Modal/CustomModal';
import { CiWallet } from 'react-icons/ci';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';
import GradiantButton from '../../../Components/Button/GradiantButton';
import { isDisabled } from '../../../utils/constants';

import { wagmiAdapterConfig } from '../../../Config/WagmiConfig';
import { useBalance } from '../../../hooks/useBalance';
import { useWalletChain } from '../../../hooks/useWalletChain';
import { WalletConnectModal } from '../modals/walletConnectModal';
import { useToken } from '../hooks/useToken';
import { sendErc20WithOzkPermit } from '../utils/sendWithOzkPermit';

function getWalletErrorDetails(error, defaultAction) {
  if (!error) {
    return { message: 'Unknown error', action: defaultAction };
  }
  if (error.shortMessage && error.action) {
    return { message: error.shortMessage, action: error.action };
  }
  const code = error.code ?? error?.cause?.code;
  if (
    code === 4001 ||
    code === 'ACTION_REJECTED' ||
    String(code) === '4001'
  ) {
    return {
      message: 'Transaction was rejected in your wallet',
      action: defaultAction,
    };
  }
  const message =
    error.message ||
    error.reason ||
    (typeof error === 'string' ? error : null) ||
    'Transaction failed';
  return { message, action: defaultAction };
}

function parseDisplayedAmount(value) {
  return Number(String(value ?? '0').replace(/,/g, ''));
}

function toTokenUnits(value, decimals) {
  return BigInt(
    BigNumber(value)
      .times(BigNumber(10).pow(decimals ?? 18))
      .integerValue(BigNumber.ROUND_FLOOR)
      .toFixed(0),
  );
}

const isNativeEthSymbol = (symbol) =>
  symbol === 'ETH' || symbol === 'ozETH';

const OZK_TRANSFER_TOKEN = {
  value: 0.0,
  logo: OZKTokenIcon,
  symbol: 'OZK',
  contract: '0xfB54B358644295Efa33bb8A903b3f1B2D86BeA7b',
  decimals: 18,
  abi: DAIToken,
};

const Transfer = () => {
  const { isConnected, address, chain } = useAccount();
  const chainId = useChainId();
  const { loadL2Balance, resetL2Balance, l2TokenBalances } = useBalance();
  const [isChecked, setIsChecked] = useState(false);
  const { tokenList } = useToken();

  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
  };

  const connections = getConnections(wagmiAdapterConfig);
  const { addOZKChain, error } = useWalletChain();

  const [transferValue, setTransferValue] = useState('');
  const [recipient, setRecipient] = useState('');
  const [selectToken, setSelectToken] = useState(false);
  const [openTransferModal, setOpenTransferModal] = useState(false);
  const [transferStatus, setTransferStatus] = useState('Waiting');
  const [walletAction, setWalletAction] = useState('Transfer is underway');
  const [isCompleted, setIsCompleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorAction, setErrorAction] = useState('');
  const [txHashL2, setTxHashL2] = useState('');
  const [walletConnectModal, setWalletConnectModal] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);

  const getL2BalanceBySymbol = (symbol) => {
    if (
      !l2TokenBalances ||
      l2TokenBalances.length === 0 ||
      l2TokenBalances.includes(undefined)
    ) {
      return 0;
    }

    const token = l2TokenBalances.find((item) => item.symbol === symbol);
    return token ? token.balance : 0;
  };

  const TokenOptions = useMemo(() => {
    const l2Tokens = new Set();
    const tokens = tokenList.reduce((acc, item) => {
      const l2Name = item.l2?.name;
      if (
        !l2Name ||
        isNativeEthSymbol(l2Name) ||
        isNativeEthSymbol(item.l1?.name)
      ) {
        return acc;
      }
      if (l2Tokens.has(l2Name)) {
        return acc;
      }
      l2Tokens.add(l2Name);
      acc.push({
        value: 0.0,
        logo: item.l2.logo,
        symbol: l2Name,
        contract: item.l2.address,
        decimals: item.l2.decimals,
        abi: item.abi,
      });
      return acc;
    }, []);

    const hasOzk = tokens.some(
      (token) =>
        token.symbol === 'OZK' ||
        token.contract?.toLowerCase() ===
          OZK_TRANSFER_TOKEN.contract.toLowerCase(),
    );
    if (!hasOzk) {
      tokens.push(OZK_TRANSFER_TOKEN);
    }
    return tokens;
  }, [tokenList]);

  useEffect(() => {
    if (!TokenOptions.length) {
      setSelectedToken(null);
      return;
    }
    setSelectedToken((prev) => {
      const match = TokenOptions.find((t) => t.symbol === prev?.symbol);
      return match || TokenOptions[0];
    });
  }, [TokenOptions]);

  useEffect(() => {
    if (!isConnected || !address || !connections) {
      resetL2Balance();
      setTransferValue('');
      return;
    }
    if (!isDisabled && TokenOptions.length) {
      loadL2Balance(address, TokenOptions);
    }
  }, [
    TokenOptions,
    isConnected,
    address,
    connections,
    isDisabled,
    resetL2Balance,
    loadL2Balance,
  ]);

  const isWalletConnect = useMemo(
    () =>
      connections &&
      connections.length > 0 &&
      connections[0].connector.id === 'walletConnect',
    [connections],
  );

  const handleSelectToken = async (itm) => {
    setSelectedToken(itm);
    setTransferValue('');
    setSelectToken(false);
  };

  const HandleTransferInput = async (value) => {
    if (value === '') {
      setTransferValue('0');
      return;
    }
    if (
      Number(value) <=
      parseDisplayedAmount(getL2BalanceBySymbol(selectedToken?.symbol))
    ) {
      setTransferValue(value);
    }
  };

  const handleTransfer = async () => {
    setErrorMessage('');
    setErrorAction('');
    setTxHashL2('');
    setIsCompleted(false);

    if (!isChecked) {
      toast.warn('You must agree to our Terms and Conditions');
      const section = document.getElementById('agreeTcTransferId');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }

    if (!connections || connections.length === 0) {
      toast.warn('No wallet connector found');
      return;
    }

    const walletProvider = await connections[0].connector.getProvider();

    if (!walletProvider) {
      toast.warn('Wallet provider not found');
      return;
    }

    const web3 = new Web3(walletProvider);
    if (!transferValue || transferValue.trim().length === 0) {
      toast.warn('Enter transfer amount');
      return;
    }
    if (Number(transferValue) <= 0) {
      toast.warn('Transfer amount must be greater than zero');
      return;
    }
    const recipientAddress = recipient.trim();
    if (!web3.utils.isAddress(recipientAddress)) {
      toast.warn('Enter a valid recipient address');
      return;
    }
    if (!selectedToken?.contract || !web3.utils.isAddress(selectedToken.contract)) {
      toast.warn('Selected token is invalid');
      return;
    }
    if (chainId !== REACT_APP_OPENZK_CHAIN_ID) {
      toast.warn('Switch Your Network To OpenZK');
      return;
    }

    const provider = new BrowserProvider(walletProvider);
    const zkProvider = new Provider(REACT_APP_OPENZK_RPC);
    const signerL2 = Signer.from(
      await provider.getSigner(),
      Number((await provider.getNetwork()).chainId),
      zkProvider,
    );

    const amount = toTokenUnits(
      transferValue,
      selectedToken?.decimals ?? 18,
    );

    setOpenTransferModal(true);
    setTransferStatus('Loading');
    setWalletAction('Confirm the transfer in your wallet');

    try {
      const tx = await sendErc20WithOzkPermit({
        signer: signerL2,
        tokenAddress: selectedToken.contract,
        recipient: recipientAddress,
        amount,
      });

      try {
        await waitForTransactionReceipt(wagmiAdapterConfig, {
          confirmation: 1,
          hash: tx.hash,
        });
      } catch (waitError) {
        const details = getWalletErrorDetails(waitError, 'Transfer');
        setErrorMessage(details.message);
        setErrorAction(details.action);
        setIsCompleted(false);
        setTransferStatus('Error');
        return;
      }

      setTxHashL2(tx.hash);
      setIsCompleted(true);
      setTransferStatus('Completed');
      loadL2Balance(address, TokenOptions);
    } catch (sendError) {
      const details = getWalletErrorDetails(sendError, 'Transfer');
      setErrorMessage(details.message);
      setErrorAction(details.action);
      setIsCompleted(false);
      setTransferStatus('Error');
    }
  };

  const setMaxByCoin = useCallback(
    (symbol) => {
      setTransferValue(String(getL2BalanceBySymbol(symbol) ?? '0'));
    },
    [l2TokenBalances],
  );

  useEffect(() => {
    if (error) {
      toast.warn(error);
    }
  }, [error]);

  useEffect(() => {
    if (isWalletConnect) {
      setWalletConnectModal(true);
    }
  }, [isWalletConnect]);

  return (
    <>
      <CustomModal
        open={openTransferModal}
        closebtn={'remove'}
        modalTitle={<h5>Wallet Actions</h5>}
        width={{ lg: '30%', md: '30%', xs: '90%', sm: '90%' }}
        modalContent={
          <Grid
            container
            lg={12}
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Grid
              container
              lg={12}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              className="p-2 rounded"
              bgcolor={'rgba(0, 0, 0, 0.32)'}
            >
              <Grid className="d-flex">
                <p>
                  {' '}
                  <CiWallet className="text-light mx-2" size={30} />{' '}
                  {walletAction}
                </p>
              </Grid>

              {transferStatus === 'Waiting' ? (
                <span style={{ color: '#ffffff' }}>Waiting</span>
              ) : transferStatus === 'Loading' ? (
                <span>
                  <CircularProgress
                    fourColor
                    style={{ color: '#FFF' }}
                    size={20}
                  />
                </span>
              ) : transferStatus === 'Completed' ? (
                <p>
                  {' '}
                  <IoMdCheckmarkCircleOutline
                    style={{ color: '#FC9D4B' }}
                    size={20}
                  />
                </p>
              ) : (
                <p>
                  <MdOutlineErrorOutline
                    style={{ color: '#FC9D4B' }}
                    size={20}
                  />
                </p>
              )}
            </Grid>

            <Grid
              container
              lg={12}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'center'}
              className="mt-4 w-100 "
            >
              {isCompleted ? (
                <p
                  className="airdrop_paratext mb-3 p-3"
                  style={{
                    border: '1px solid #ffffff',
                    borderRadius: '25px',
                    textAlign: 'center',
                  }}
                >
                  {`${transferValue} ${selectedToken?.symbol} transfer initiated`}
                </p>
              ) : (
                transferStatus === 'Error' && (
                  <p
                    className="airdrop_paratext mb-3 p-3"
                    style={{
                      border: '1px solid #ffffff',
                      borderRadius: '25px',
                      textAlign: 'center',
                    }}
                  >
                    Error: {`${errorMessage} - action: ${errorAction}`}
                  </p>
                )
              )}{' '}
              {txHashL2 !== '' ? (
                <>
                  <a
                    href={
                      REACT_APP_BLOCK_EXPLORER_OPEN_ZK_URL + 'tx/' + txHashL2
                    }
                    target="blank"
                    style={{
                      color: '#fff',
                      width: '70%',
                      marginBottom: '20px',
                    }}
                  >
                    <GradiantButton
                      btnTextColor="#FFF"
                      text={'VIEW TRANSACTION'}
                      borderColor={'#FC9D4B'}
                      width="100%"
                    />
                  </a>

                  <GradiantButton
                    btnTextColor="#FFF"
                    text={'CLOSE'}
                    borderColor={'#FC9D4B'}
                    width="70%"
                    onClick={() => {
                      setOpenTransferModal(false);
                      setTransferValue('');
                      setTxHashL2('');
                      setIsCompleted(false);
                      setTransferStatus('Waiting');
                    }}
                  />
                </>
              ) : (
                <GradiantButton
                  btnTextColor="#FFF"
                  text={'CLOSE'}
                  borderColor={'#FC9D4B'}
                  width="100%"
                  onClick={() => {
                    setOpenTransferModal(false);
                    setTxHashL2('');
                    setIsCompleted(false);
                    setTransferStatus('Waiting');
                  }}
                />
              )}
            </Grid>
          </Grid>
        }
      />

      <Grid
        item
        className="w-100"
        lg={12}
        md={12}
        sm={12}
        xs={12}
        display={'flex'}
        flexDirection={'column'}
        alignItems={'flex-start'}
        justifyContent={'flex-start'}
      >
        <Grid
          item
          className="BridgeGlassCard px-3 py-2 w-full"
          height={'100%'}
          lg={12}
          md={12}
          sm={12}
          xs={12}
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          justifyContent={'center'}
        >
          <Grid
            item
            className="w-100 mt-2"
            lg={12}
            md={12}
            sm={12}
            xs={12}
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Grid
              item
              lg={12}
              md={12}
              sm={12}
              xs={12}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'center'}
              gap={'8px'}
            >
              <div className="flex flex-col flex-[1_0_0] gap-1">
                <p className="BridgeSwapLabel">From</p>
                <Grid
                  item
                  className="md:px-[14px] px-[8px] py-[7px] BridgeInputBg h-[53px]"
                  display={'flex'}
                  flexDirection={'row'}
                  alignItems={'center'}
                  justifyContent={'start'}
                  style={{
                    background: '#28282B',
                    borderTopRightRadius: '14px',
                    borderBottomRightRadius: '14px',
                  }}
                >
                  <p className="BridgeSwapText m-0 p-0">OpenZK</p>
                  <img
                    src={OZKLogo}
                    alt="OpenZK"
                    width={'16px'}
                    height={'16px'}
                    className="ml-1"
                  />
                </Grid>
              </div>

              <div className="flex p-[16px] justify-center items-center gap-2.5 rounded-[12px] border border-white/10 bg-[#5773FF] mt-auto">
                <img src={DoubleArrow} alt="arrows" />
              </div>
              <div className="flex flex-col flex-[1_0_0] gap-1">
                <p className="BridgeSwapLabel">to</p>
                <Grid
                  item
                  className="md:px-[14px] px-[8px] py-[7px] BridgeInputBg h-[53px]"
                  display={'flex'}
                  flexDirection={'row'}
                  alignItems={'center'}
                  justifyContent={'start'}
                  style={{
                    background: '#28282B',
                    borderTopRightRadius: '14px',
                    borderBottomRightRadius: '14px',
                  }}
                >
                  <p className="BridgeSwapText m-0 p-0">OpenZK</p>
                  <img
                    src={OZKLogo}
                    alt="OpenZK"
                    width={'16px'}
                    height={'16px'}
                    className="ml-1"
                  />
                </Grid>
              </div>
            </Grid>
          </Grid>

          <Grid
            item
            className="w-100 mt-3"
            lg={12}
            md={12}
            sm={12}
            xs={12}
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Grid
              item
              className="BridgeInputBg"
              lg={12}
              md={12}
              sm={12}
              xs={12}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <Grid item className="w-100">
                <input
                  type="text"
                  className="p-2"
                  style={{
                    background: 'transparent',
                    color: '#ffffff',
                    width: '100%',
                    border: '0',
                    borderTopLeftRadius: '12px',
                    borderBottomLeftRadius: '12px',
                  }}
                  placeholder={'Recipient address'}
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            className="w-100 mt-3"
            lg={12}
            md={12}
            sm={12}
            xs={12}
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Grid
              item
              className="BridgeInputBg"
              lg={12}
              md={12}
              sm={12}
              xs={12}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <Grid item className="w-100">
                <input
                  type="number"
                  className="p-2"
                  style={{
                    background: 'transparent',
                    color: '#ffffff',
                    width: '100%',
                    border: '0',
                    borderTopLeftRadius: '12px',
                    borderBottomLeftRadius: '12px',
                  }}
                  placeholder={'Enter Amount'}
                  value={transferValue}
                  onChange={(e) => HandleTransferInput(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
              </Grid>

              <Grid
                item
                className="p-2 px-4"
                display={'flex'}
                flexDirection={'row'}
                alignItems={'center'}
                justifyContent={'center'}
                style={{
                  cursor: 'pointer',
                  background: '#28282B',
                  borderTopRightRadius: '12px',
                  borderBottomRightRadius: '12px',
                }}
                onClick={() => setSelectToken(true)}
              >
                <p className="BridgeInputText m-0 p-0">
                  {' '}
                  {selectedToken?.symbol || 'Token'}{' '}
                </p>{' '}
                {selectedToken?.logo ? (
                  <img
                    src={selectedToken.logo}
                    alt={selectedToken.symbol}
                    width={'25px'}
                    height={'25px'}
                    className="mx-1"
                  />
                ) : null}
                <MdOutlineKeyboardArrowDown
                  className="mx-1"
                  size={'43px'}
                  style={{ color: '#ffffff' }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            className="w-100 mt-4"
            lg={12}
            md={12}
            sm={12}
            xs={12}
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'flex-start'}
          >
            <p className="BridgeInputText m-0 p-0">Balance :</p>
            <p className="ml-2 m-0 p-0" style={{ fontSize: '16px' }}>
              {isConnected
                ? getL2BalanceBySymbol(selectedToken?.symbol)
                : '0'}{' '}
            </p>
            {selectedToken?.logo ? (
              <img
                src={selectedToken.logo}
                alt={selectedToken.symbol}
                width={'20px'}
                height={'20px'}
                className="mx-1"
              />
            ) : null}
            <Grid
              item
              style={{ cursor: 'pointer' }}
              onClick={() => setMaxByCoin(selectedToken?.symbol)}
            >
              <span
                className="mx-2"
                style={{ fontSize: '14px', color: '#FF9854' }}
              >
                Max
              </span>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        className="mt-0 w-100"
        xs={12}
        gap="20px"
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        alignContent={'center'}
        justifyContent={'center'}
      >
        <Grid
          item
          xs={12}
          className="mt-2 w-100"
          display={'flex'}
          flexDirection={'row'}
          alignItems={'center'}
          justifyContent={'center'}
        >
          {!isConnected ? (
            <ConnectButton network="openzk" />
          ) : chain?.id !== REACT_APP_OPENZK_CHAIN_ID ? (
            <Button
              className="mt-2"
              sx={{
                height: '40px',
                width: 'auto',
                backgroundColor: '#000000',
                border: 0.75,
                borderColor: '#FC9D4B',
                borderRadius: '10px',
                color: '#FFE584',
                fontWeight: 'semibold',
                fontFamily: 'PoppinsRegular',
                boxShadow: '0px 0px 13px 1px #FC9D4B',
                fontSize: {
                  xs: '10px',
                  sm: '16px',
                  md: '18px',
                  lg: '18px',
                },
                padding: {
                  xs: '15px',
                  sm: '12px',
                  md: '15px',
                },
                '&:hover': {
                  background:
                    'linear-gradient(90deg, rgba(241, 215, 75, 0.8) 0%, rgba(221, 113, 42, 0.8) 65.9%)',
                  color: '#ffffff',
                },
                textTransform: 'none',
              }}
              onClick={() => {
                !isDisabled && !isWalletConnect && addOZKChain();
              }}
            >
              {`Switch ${isWalletConnect ? 'your browser & wallet' : ''} to OpenZK Network`}
            </Button>
          ) : (
            <Button
              className="mt-2"
              sx={{
                height: '40px',
                width: 'auto',
                backgroundColor: '#000000',
                border: 0.75,
                borderColor: '#FC9D4B',
                borderRadius: '10px',
                color: '#FFE584',
                fontWeight: 'semibold',
                fontFamily: 'PoppinsRegular',
                boxShadow: '0px 0px 13px 1px #FC9D4B',
                fontSize: {
                  xs: '10px',
                  sm: '16px',
                  md: '18px',
                  lg: '18px',
                },
                padding: {
                  xs: '15px',
                  sm: '12px',
                  md: '15px',
                },
                '&:hover': {
                  background:
                    'linear-gradient(90deg, rgba(241, 215, 75, 0.8) 0%, rgba(221, 113, 42, 0.8) 65.9%)',
                  color: '#ffffff',
                },
                textTransform: 'none',
              }}
              onClick={() => {
                if (!isDisabled) {
                  handleTransfer();
                }
              }}
            >
              Transfer
            </Button>
          )}
        </Grid>

        <Grid
          item
          lg={12}
          md={12}
          flexDirection={'row'}
          alignItems={'center'}
          justifyContent={'center'}
        >
          {' '}
          <p style={{ textAlign: 'center' }} id="agreeTcTransferId">
            {' '}
            <Checkbox
              style={{ color: 'green' }}
              checked={isChecked}
              onClick={handleCheckboxChange}
            />{' '}
            Agree to our{' '}
            <a
              href="/pdf/OpenZK_Terms.of.Use_250620.pdf"
              target="_blank"
              className="landingPtag"
            >
              {' '}
              Terms and Conditions of Use{' '}
            </a>{' '}
            and our{' '}
            <a href="/privacypolicy" target="_blank" className="landingPtag">
              Privacy Policy.{' '}
            </a>
          </p>{' '}
        </Grid>
      </Grid>

      <WalletConnectModal
        open={walletConnectModal}
        close={() => setWalletConnectModal(false)}
      />

      <CustomModal
        open={selectToken}
        setOpen={setSelectToken}
        modalTitle={<h4>Select Tokens</h4>}
        width={{ lg: '30%', md: '30%', xs: '90%', sm: '90%' }}
        modalContent={
          <Grid
            container
            lg={12}
            display={'flex'}
            flexDirection={'column'}
            alignItems={'flex-start'}
            justifyContent={'flex-start'}
            className="m-0 p-0"
          >
            <Grid
              item
              className="px-3 w-100"
              lg={12}
              md={12}
              sm={12}
              xs={12}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <Grid
                item
                className="mb-3 tokenListScroll"
                lg={11}
                md={11}
                sm={11}
                xs={11}
                display={'flex'}
                flexDirection={'column'}
                alignItems={'center'}
                style={{
                  maxHeight: '60vh',
                  overflowY: 'auto',
                  width: '100%',
                  paddingRight: '8px',
                }}
              >
                {TokenOptions && TokenOptions?.length > 0
                  ? TokenOptions.map((itm, i) => {
                      return (
                        <Grid
                          item
                          key={i}
                          className="w-100 p-3 mt-2"
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          display={'flex'}
                          flexDirection={'row'}
                          alignItems={'flex-start'}
                          justifyContent={'space-between'}
                          style={{
                            background: '#FFFFFF0F',
                            borderRadius: '15px',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            handleSelectToken(itm);
                          }}
                        >
                          <Grid
                            item
                            display={'flex'}
                            flexDirection={'row'}
                            alignItems={'center'}
                          >
                            <img
                              src={itm?.logo}
                              alt="tokenlogo"
                              width={'30px'}
                              height={'30px'}
                              className="mx-2"
                            />
                            <p className="airdrop_text">{itm?.symbol}</p>
                          </Grid>
                          <p className="airdrop_text">
                            {isConnected
                              ? getL2BalanceBySymbol(itm?.symbol)
                              : '0'}
                          </p>
                        </Grid>
                      );
                    })
                  : ''}
              </Grid>
            </Grid>
          </Grid>
        }
      />
    </>
  );
};

export default Transfer;
